import classNames from 'classnames';
import type { FC, PropsWithChildren, ReactNode } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyS, LabelXS } from '@sorare/core/src/atoms/typography';
import { fantasy } from '@sorare/core/src/lib/glossary';

import { messages } from '#src/components/lineup/LineupBonusDetails/AppearanceBonusBreakdown/messages.ts';

const Content = styled(Vertical)`
  &.empty {
    filter: grayscale(1);
    opacity: 0.8;
  }
`;
const BrandBodyS = styled(BodyS)`
  font-family: var(--brand-font);
`;
const BrandLabelXS = styled(LabelXS)`
  font-family: var(--brand-font);
`;
const Footer = styled(Vertical).attrs({ center: true })`
  min-width: 90px;

  > ${Vertical} {
    width: 100%;
  }
`;

type Props = {
  border: boolean;
  percentBasisPoints: number | null;
  points: number | null;
  label: ReactNode;
  bar: ReactNode;
  cta?: ReactNode;
  Icon: FC<PropsWithChildren<{ children: ReactNode; border: boolean }>>;
  lightBackground?: boolean;
};

export const BreakdownBlock = ({
  border,
  percentBasisPoints,
  points,
  label,
  bar,
  cta,
  Icon,
  lightBackground,
}: Props) => {
  return (
    <Vertical center gap={2}>
      <Content
        center
        className={classNames({ empty: !percentBasisPoints && !points })}
      >
        <Icon border={border}>
          {typeof percentBasisPoints === 'number' ? (
            <BrandBodyS
              bold
              color={lightBackground ? 'var(--c-black)' : 'var(--c-white)'}
            >
              <Horizontal center gap={0}>
                <FormattedMessage
                  {...messages.numberPercent}
                  values={{
                    number: (
                      <FormattedNumber
                        value={percentBasisPoints / 100}
                        maximumFractionDigits={1}
                        signDisplay="always"
                      />
                    ),
                    span: (...chunks: ReactNode[]) => (
                      <BrandBodyS
                        bold
                        color={
                          lightBackground
                            ? 'var(--c-nl-600)'
                            : 'var(--c-nd-600)'
                        }
                      >
                        {chunks}
                      </BrandBodyS>
                    ),
                  }}
                />
              </Horizontal>
            </BrandBodyS>
          ) : (
            <Vertical center gap={0}>
              <BrandBodyS
                bold
                className="text-center"
                color={lightBackground ? 'var(--c-black)' : 'var(--c-white)'}
              >
                <FormattedMessage
                  {...fantasy.ordinalPoints}
                  values={{
                    score: (
                      <FormattedNumber
                        value={points || 0}
                        maximumFractionDigits={1}
                        signDisplay="always"
                      />
                    ),
                    sup: (...chunks: ReactNode[]) => (
                      <BrandLabelXS
                        bold
                        uppercase
                        color={
                          lightBackground
                            ? 'var(--c-nl-600)'
                            : 'var(--c-nd-600)'
                        }
                      >
                        {chunks}
                      </BrandLabelXS>
                    ),
                  }}
                />
              </BrandBodyS>
            </Vertical>
          )}
        </Icon>
        <Footer>
          {label}
          {bar}
        </Footer>
      </Content>
      {cta}
    </Vertical>
  );
};
